import { z } from 'zod';

/**
 * Beneficial owner data structure for Form A
 */
export interface BeneficialOwner {
  lastName: string;
  firstName: string;
  address: string;
  postcode: string;
  city: string;
  country: string;
  birthDate?: Date;
  nationality: string;
}

/**
 * Create validation schema with translations for Form A
 */
export const createFormASchema = (tForms: (key: string) => string) =>
  z.object({
    // A.1. Erklärungen - Beneficial Owner Type (Radio Group)
    beneficialOwnerType: z
      .string()
      .min(1, tForms('kyc.validation.beneficialOwnerTypeRequired')),

    // A.2. Wirtschaftlich berechtigte Person – Angaben
    beneficialOwners: z
      .array(
        z.object({
          lastName: z
            .string()
            .min(1, tForms('kyc.validation.lastNameRequired')),
          firstName: z
            .string()
            .min(1, tForms('kyc.validation.firstNameRequired')),
          address: z.string().min(1, tForms('kyc.validation.addressRequired')),
          postcode: z
            .string()
            .min(1, tForms('kyc.validation.postcodeRequired')),
          city: z.string().min(1, tForms('kyc.validation.cityRequired')),
          country: z
            .string()
            .min(1, tForms('kyc.validation.countryRequired'))
            .length(2, tForms('kyc.validation.countryInvalidFormat')),
          birthDate: z.date({
            message: tForms('kyc.validation.birthDateRequired'),
          }),
          nationality: z
            .string()
            .min(1, tForms('kyc.validation.nationalityRequired'))
            .length(2, tForms('kyc.validation.nationalityInvalidFormat')),
        })
      )
      .default([]),

    // A.3. Herkunft der Vermögenswerte
    originOfAssets: z.array(z.string()).optional(),
    originOfAssetsOther: z.string().optional(),
  });

/**
 * Inferred type for Form A data from the Zod schema
 */
export type FormAData = z.infer<ReturnType<typeof createFormASchema>>;

/**
 * Props interface for Form A component
 */
export interface FormAProps {
  onSubmit?: (data: FormAData) => void;
  onError?: (error: Error) => void;
  onChange?: (data: Partial<FormAData>) => void;
  className?: string;
  defaultValues?: Partial<FormAData>;
}

/**
 * Ref handle for Form A component
 */
export interface FormAHandle {
  triggerValidation: () => Promise<boolean>;
}

/**
 * Beneficial owner types
 */
export type BeneficialOwnerType = 'sole' | 'others' | '';
